<template>
  <div class="field-map-wrapper" :class="{ 'field-map-wrapper--compact': compact }">
    <WayMap
      ref="map"
      :items="mapItems"
      :type-styles="typeStyles"
      :show-accuracy="false"
      :initial-center="defaultCenter"
      :initial-zoom="defaultZoom"
      :highlight-id="selectedFindingId"
      @marker-click="onMarkerClick"
      @layer-ready="onMapReady"
    />

    <div v-if="!mapItems.length" class="empty-overlay">
      <v-icon size="40" color="grey-darken-1">mdi-map-marker-off-outline</v-icon>
      <div class="empty-text">No findings logged yet for this inspection.</div>
      <div class="empty-sub">Use “Create Finding” at the top to add one.</div>
    </div>
  </div>
</template>

<script>
import WayMap from "@/components/WayMap.vue";
import { icons, iconsNums } from "@/assets/icons";

export default {
  name: "InspectionFieldMap",
  components: { WayMap },
  props: {
    inspection: { type: Object, default: null },
    findings: { type: Array, default: () => [] },
    formVisualsById: { type: Object, default: () => ({}) },
    compact: { type: Boolean, default: false },
  },
  emits: ["marker-click"],
  data() {
    return {
      selectedFindingId: null,
      typeStyles: {
        finding: { color: "#f2994a", icon: "fas fa-exclamation-circle" },
      },
    };
  },
  computed: {
    defaultCenter() {
      const lat =
        this.inspection?.startLatitude ?? this.inspection?.StartLatitude ?? 53.4;
      const lng =
        this.inspection?.startLongitude ?? this.inspection?.StartLongitude ?? -7.9;
      return [Number(lat) || 53.4, Number(lng) || -7.9];
    },
    defaultZoom() {
      return this.inspection?.startLatitude ? 15 : 7;
    },
    mapItems() {
      return (this.findings || [])
        .map((f) => {
          const lat = f.lat ?? f.latitude ?? f.Latitude;
          const lng = f.lng ?? f.longitude ?? f.Longitude;
          if (lat == null || lng == null) return null;
          const visual = this.resolveVisuals(f);
          return {
            id: f.findingId ?? f.FindingId,
            type: "finding",
            title: f.details || f.title || "Finding",
            lat: +lat,
            lng: +lng,
            IconName: visual.iconName,
            iconColour: visual.iconColour,
          };
        })
        .filter(Boolean);
    },
  },
  watch: {
    findings: {
      deep: true,
      handler() {
        // Re-fit when findings are reloaded (e.g. after a save) so any new
        // pin lands inside the visible area.
        this.$nextTick(() => {
          if (this.mapItems.length > 0) {
            this.$refs.map?.fitToItems?.(0.15);
          }
        });
      },
    },
  },
  methods: {
    firstNonEmpty(...vals) {
      for (const v of vals) {
        if (typeof v === "string" && v.trim()) return v.trim();
      }
      return "";
    },
    legacyIconLookup(code) {
      const c = this.firstNonEmpty(code);
      if (!c) return "";
      const idx = iconsNums.findIndex((e) => e.icon === c);
      return idx >= 0 ? icons[idx]?.icon || "" : "";
    },
    resolveVisuals(f) {
      const formId = f?.formId ?? f?.FormId;
      const meta = formId != null ? this.formVisualsById[String(formId)] : null;
      const iconName =
        this.firstNonEmpty(
          f.iconName,
          f.IconName,
          meta?.iconName,
          this.legacyIconLookup(f.icon ?? f.Icon)
        ) || this.typeStyles.finding.icon;
      const iconColour =
        this.firstNonEmpty(f.iconColour, f.IconColour, meta?.iconColour) ||
        this.typeStyles.finding.color;
      return { iconName, iconColour };
    },
    onMapReady() {
      this.$nextTick(() => {
        if (this.mapItems.length > 0) {
          this.$refs.map?.fitToItems?.(0.15);
        }
      });
    },
    onMarkerClick({ id, type, item }) {
      if (type !== "finding") return;
      this.selectedFindingId = id;
      this.$emit("marker-click", { id, item });
    },
  },
};
</script>

<style scoped>
.field-map-wrapper {
  position: relative;
  width: 100%;
  height: 75vh;
  min-height: 540px;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e8edf0;
  background: #f3f4f6;
}

.field-map-wrapper--compact {
  height: 360px;
  min-height: 0;
  border-radius: 0;
  border: 0;
}

.empty-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: rgba(248, 251, 249, 0.85);
  color: #475467;
  pointer-events: none;
}

.empty-text {
  font-size: 15px;
  font-weight: 600;
  color: #182230;
}

.empty-sub {
  font-size: 13px;
  color: #667085;
}
</style>
